import React from "react";
import { Mail, Phone, Linkedin, Github, MapPin } from "lucide-react";
import { useInView } from "../hooks/useInView";

const links = [
  {
    icon: Mail,
    label: "Email",
    value: "akalankadewmith@gmail.com",
    href: "mailto:akalankadewmith@gmail.com",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+94 77 620 9217",
    href: "tel:+94776209217",
  },
  {
    icon: Linkedin,
    label: "LinkedIn",
    value: "Dewmith Akalanka",
    href: "https://linkedin.com/in/dewmith-akalanka",
  },
  {
    icon: Github,
    label: "GitHub",
    value: "dewMyth",
    href: "https://github.com/dewMyth",
  },
  {
    icon: MapPin,
    label: "Location",
    value: "Battaramulla, Sri Lanka",
    href: null,
  },
];

export default function Contact() {
  const [ref, inView] = useInView(0.1);

  return (
    <section
      id="contact"
      ref={ref}
      style={{
        padding: "clamp(80px, 10vw, 140px) clamp(24px, 8vw, 120px)",
        background: "var(--ink)",
        color: "var(--cream)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "clamp(48px, 6vw, 100px)",
          }}
        >
          {/* Left */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "none" : "translateY(24px)",
              transition: "all 0.6s ease",
            }}
          >
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(200,96,42,0.9)",
                marginBottom: 20,
              }}
            >
              06 — Contact
            </p>
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(36px, 5vw, 64px)",
                lineHeight: 1.0,
                letterSpacing: "-0.03em",
                marginBottom: 28,
              }}
            >
              Let's work
              <br />
              <em style={{ color: "rgba(255,255,255,0.35)" }}>together.</em>
            </h2>
            <div
              style={{
                width: 48,
                height: 2,
                background: "rgba(200,96,42,0.8)",
                marginBottom: 32,
              }}
            />
            <p
              style={{
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.8,
                fontWeight: 300,
                fontSize: 15,
                maxWidth: 380,
              }}
            >
              I'm open to interesting opportunities — whether it's a new role, a
              freelance project, or just a conversation about distributed
              systems and backend architecture.
            </p>

            <a
              href="mailto:akalankadewmith@gmail.com"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                marginTop: 40,
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--ink)",
                background: "var(--cream)",
                padding: "14px 32px",
                borderRadius: 2,
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--accent-warm)";
                e.currentTarget.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--cream)";
                e.currentTarget.style.color = "var(--ink)";
              }}
            >
              <Mail size={14} strokeWidth={1.5} /> Say Hello
            </a>
          </div>

          {/* Right */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "none" : "translateY(24px)",
              transition: "all 0.6s ease 0.2s",
            }}
          >
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.3)",
                marginBottom: 28,
              }}
            >
              Find me at
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {links.map(({ icon: Icon, label, value, href }) => {
                const inner = (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      padding: "20px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Icon
                      size={16}
                      strokeWidth={1.5}
                      style={{ color: "rgba(200,96,42,0.7)", flexShrink: 0 }}
                    />
                    <div>
                      <p
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "rgba(255,255,255,0.3)",
                          marginBottom: 4,
                        }}
                      >
                        {label}
                      </p>
                      <p
                        style={{
                          fontSize: 14,
                          color: "rgba(255,255,255,0.75)",
                          fontWeight: 300,
                        }}
                      >
                        {value}
                      </p>
                    </div>
                  </div>
                );

                return href ? (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    style={{ transition: "opacity 0.2s" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "0.7";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={label}>{inner}</div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: 80,
            paddingTop: 32,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 24,
          }}
        >
          <img
            src="/signature.png"
            alt="Dewmith Akalanka signature"
            style={{
              height: 48,
              width: "auto",
              filter: "brightness(0) invert(1)",
              opacity: 0.25,
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 4,
            }}
          >
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "rgba(255,255,255,0.2)",
              }}
            >
              © {new Date().getFullYear()} Dewmith Akalanka. All rights
              reserved.
            </p>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "rgba(255,255,255,0.2)",
              }}
            >
              Built with React · Deployed on Railway
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
